// src/pages/Admin/Adherents/ReglementInscription.jsx
import React, { useEffect, useState, useMemo } from "react";
import { getReglementInscriptionByIdAndSaison } from "../../../services/reglementinscriptionServices";

export default function ReglementInscription({ adherentId, saison }) {
  const [reglement, setReglement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adherentId || !saison) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getReglementInscriptionByIdAndSaison(adherentId, saison);
        setReglement(data || null);
      } catch (e) {
        console.error("Erreur getReglementInscription:", e);
        setReglement(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId, saison]);

  const eur = (n) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
      Number.isFinite(+n) ? +n : 0
    );

  const amounts = useMemo(() => {
    const lic = parseFloat(reglement?.montant_lic_assurance) || 0;
    const cot = parseFloat(reglement?.montant_cotisation) || 0;
    const redFoyer = parseFloat(reglement?.reduction_foyer) || 0;
    const pass = parseFloat(reglement?.pass_sport) || 0;
    const passClub32 = parseFloat(reglement?.passclub32) || 0;
    const redSaison = parseFloat(reglement?.reduction_saison) || 0;
    const brut = lic + cot;
    const reductions = redFoyer + pass + redSaison + passClub32;
    const net = Math.max(0, brut + reductions);
    return { lic, cot, redFoyer, pass, redSaison, passClub32, brut, reductions, net };
  }, [reglement]);

  const StatusBadge = ({ status }) => {
    const map = {
      "Réglé": "bg-emerald-100 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50",
      "En cours": "bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800/50",
      "Non réglé": "bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800/50",
      "Problème": "bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:ring-fuchsia-800/50",
    };
    const cls =
      map[status] ||
      "bg-slate-100 text-slate-900 ring-slate-200 dark:bg-slate-800/40 dark:text-slate-100 dark:ring-slate-700/50";
    return (
      <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 ${cls}`}>
        {status || "—"}
      </span>
    );
  };

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
            Règlement d’inscription
          </h3>
          {reglement && <StatusBadge status={reglement.statut_reglement} />}
        </div>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
              />
            ))}
          </div>
        ) : !reglement ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun règlement trouvé pour la saison <span className="font-semibold">{saison}</span>.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {/* Saison + type + nb règlements */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <InfoRow label="Saison" value={reglement.saison || "—"} />
                <InfoRow label="Type de règlement" value={reglement.type_reglement || "—"} />
                <InfoRow label="Nombre d’échéances" value={reglement.nb_reglement ?? "—"} />
              </div>
            </div>

            {/* Montants — Décomposition */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
                Décomposition des montants
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Brut */}
                <div className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-white dark:bg-slate-900/60">
                  <LineItem label="Licence + Assurance" value={eur(amounts.lic)} />
                  <LineItem label="Cotisation" value={eur(amounts.cot)} />
                  <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Total brut</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{eur(amounts.brut)}</span>
                  </div>
                </div>

                {/* Réductions */}
                <div className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-white dark:bg-slate-900/60">
                  <LineItem label="Réduction foyer" value={` ${eur(amounts.redFoyer)}`} />
                  <LineItem label="Pass’Sport" value={` ${eur(amounts.pass)}`} />
                  <LineItem label="Réduction saison" value={` ${eur(amounts.redSaison)}`} />
                  <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Total réductions</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                       {eur(amounts.reductions)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Net à payer */}
              <div className="mt-4 flex items-center justify-between rounded-lg p-3 ring-1 ring-emerald-200 dark:ring-emerald-800/60 bg-emerald-50 dark:bg-emerald-900/30">
                <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                  Net à payer
                </span>
                <span className="text-base sm:text-lg font-bold text-emerald-900 dark:text-emerald-200">
                  {eur(amounts.net)}
                </span>
              </div>
            </div>

            {/* Observations */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                Observations
              </div>
              <div className="text-sm text-slate-800 dark:text-slate-100">
                {reglement.observations?.trim() || "Aucune"}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* Petits sous-composants utilitaires */
function InfoRow({ label, value }) {
  return (
    <div className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-white dark:bg-slate-900/60 flex items-center justify-between">
      <span className="text-xs text-slate-600 dark:text-slate-300">{label}</span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  );
}

function LineItem({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-700 dark:text-slate-200">{label}</span>
      <span className="text-sm text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  );
}
